import { memo, useCallback, useMemo } from "react";

import classes from "./TextCell.module.css";
import classesCell from "./BodyCell.module.css";
import { truncateString } from "../../../lib/script";
import { statusCellWorkQ } from "../../../lib/workQ";
import { getAppointmentStatusLabel } from "../../../lib/labels";
import StatusCellInnerV2 from "./StatusCellInnerV2";
import { useWorkQShell, WorkQHoverHint } from "../context/workQShellContext";
import WorkQDeferredTooltip from "./WorkQDeferredTooltip";

function TextCell({ text, group, itemKey, caseData }) {
    const shell = useWorkQShell();

    const displayText = useMemo(() => {
        const isAppointmentType =
            itemKey === "appointment_type" ||
            (typeof itemKey === "string" && itemKey.endsWith("_appointment_type"));
        return isAppointmentType ? getAppointmentStatusLabel(text) : text;
    }, [itemKey, text]);

    const truncateText = useMemo(
        () => (displayText && displayText.length > 20 ? truncateString(displayText, 20) : null),
        [displayText],
    );

    const attyStatus = useMemo(() => {
        if (itemKey != "attorney_firm") {
            return null;
        }
        return statusCellWorkQ("attorney_status", caseData, group?.status_id, undefined);
    }, [itemKey, caseData, group?.status_id]);

    const truncatedHover = useCallback(
        (fullText, inner) => {
            if (shell) {
                return (
                    <WorkQHoverHint message={fullText} isFlex={itemKey === "attorney_firm"}>
                        {inner}
                    </WorkQHoverHint>
                );
            }
            return (
                <WorkQDeferredTooltip normalEvent={true} messages={[fullText]} wrapperClassName={classes.tooltip_wrapper}>
                    {inner}
                </WorkQDeferredTooltip>
            );
        },
        [shell, itemKey],
    );

    if (itemKey == "attorney_firm") {
        return (
            <>
                {!truncateText && (
                    <div className={`${classes.text_cell} ${classesCell.body_cell} ${classesCell.status_cell}`}>
                        <StatusCellInnerV2
                            itemKey={itemKey}
                            caseData={caseData}
                            text={attyStatus}
                            classes={classes}
                            classesCell={classesCell}
                            noDataCell={false}
                        >
                            {text ? text : "No Atty on file"}
                        </StatusCellInnerV2>
                    </div>
                )}

                {truncateText && (
                    <div className={`${classes.text_cell} ${classesCell.body_cell} ${classesCell.status_cell}`}>
                        {truncatedHover(
                            text,
                            <StatusCellInnerV2
                                itemKey={itemKey}
                                caseData={caseData}
                                text={attyStatus}
                                classes={classes}
                                classesCell={classesCell}
                                noDataCell={false}
                            >
                                {truncateText ? truncateText : "No Atty on file"}
                            </StatusCellInnerV2>,
                        )}
                    </div>
                )}
            </>
        );
    }

    if (itemKey != "attorney_firm") {
        return (
            <>
                {!truncateText && (
                    <div className={`${classes.text_cell} ${classesCell.body_cell}`}>{displayText}</div>
                )}

                {truncateText && (
                    <div className={`${classes.text_cell} ${classesCell.body_cell}`}>
                        {truncatedHover(
                            displayText,
                            <div className={classes.wrapper}>{truncateText}</div>,
                        )}
                    </div>
                )}
            </>
        );
    }
}

function textCellPropsEqual(prevProps, nextProps) {
    if (prevProps.text !== nextProps.text) return false;
    if (prevProps.itemKey !== nextProps.itemKey) return false;

    if (nextProps.itemKey === "attorney_firm") {
        if (prevProps.group?.status_id !== nextProps.group?.status_id) return false;
        if (prevProps.caseData?.additional?.no_attorney_reason !== nextProps.caseData?.additional?.no_attorney_reason) {
            return false;
        }
    }

    return true;
}

export default memo(TextCell, textCellPropsEqual);
